/**
 * Groups items into the folder tree they were uploaded with, from each item's relative path
 * ("Controls testing/AP-02/Evidences/a.pdf"). Items without a path sit at the root.
 */

export type PathNode<T> = {
  name: string;
  path: string;
  folders: PathNode<T>[];
  items: T[];
  /** Items under this folder, at any depth. */
  count: number;
};

export function buildPathTree<T>(items: T[], pathOf: (item: T) => string | null | undefined): PathNode<T> {
  const root: PathNode<T> = { name: '', path: '', folders: [], items: [], count: 0 };
  for (const item of items) {
    const parts = (pathOf(item) || '').replace(/\\/g, '/').split('/').filter(Boolean);
    const dirs = parts.slice(0, -1);
    let node = root;
    node.count += 1;
    dirs.forEach((dir, i) => {
      const path = dirs.slice(0, i + 1).join('/');
      let child = node.folders.find((f) => f.path === path);
      if (!child) {
        child = { name: dir, path, folders: [], items: [], count: 0 };
        node.folders.push(child);
      }
      child.count += 1;
      node = child;
    });
    node.items.push(item);
  }
  const sortNode = (n: PathNode<T>) => {
    n.folders.sort((a, b) => a.name.localeCompare(b.name));
    n.folders.forEach(sortNode);
  };
  sortNode(root);
  return root;
}

/** Folder paths down to `depth` levels, for the initially open folders. */
export function foldersToDepth<T>(node: PathNode<T>, depth: number): string[] {
  if (depth <= 0) return [];
  return node.folders.flatMap((f) => [f.path, ...foldersToDepth(f, depth - 1)]);
}

/** Every folder path in the tree (to open all while searching). */
export function allFolderPaths<T>(node: PathNode<T>): string[] {
  return node.folders.flatMap((f) => [f.path, ...allFolderPaths(f)]);
}
